import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiCalendar,
  FiSave,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeeSelector
  from "../common/EmployeeSelector";

import {
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeLeave.css";


const INITIAL_FORM = {
  employeeId: "",
  leaveTypeId: "",
  fromDate: "",
  toDate: "",
  halfDay: false,
  halfDaySession: "FIRST_HALF",
  reason: "",
  remark: "",
};


const AddLeaveModal = ({
  open,

  employees = [],

  leaveTypes = [],

  onClose,

  onSuccess,
}) => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    form,
    setForm,
  ] =
    useState({
      ...INITIAL_FORM,
    });


  const [
    errors,
    setErrors,
  ] =
    useState({});


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    balanceLoading,
    setBalanceLoading,
  ] =
    useState(false);


  const [
    balances,
    setBalances,
  ] =
    useState([]);


  useEffect(
    () => {

      if (
        open
      ) {

        setForm({
          ...INITIAL_FORM,
        });


        setErrors(
          {}
        );


        setBalances(
          []
        );

      }

    },
    [
      open,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | LOAD BALANCE
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      if (
        !open ||
        !form.employeeId
      ) {

        setBalances(
          []
        );

        return;

      }


      const loadBalance =
        async () => {

          try {

            setBalanceLoading(
              true
            );


            const year =
              form.fromDate
                ?
                new Date(
                  form.fromDate
                )
                  .getFullYear()
                :
                new Date()
                  .getFullYear();


            const response =
              await api.get(
                config.getEmployeeLeaveBalances(
                  form.employeeId,
                  year
                )
              );


            if (
              response?.status ===
              "success"
            ) {

              setBalances(
                Array.isArray(
                  response?.data
                )
                  ?
                  response.data
                  :
                  response
                    ?.data
                    ?.balances ||
                  []
              );

            }

          } catch (
            requestError
          ) {

            console.error(
              "Leave balance error:",
              requestError
            );


            setBalances(
              []
            );

          } finally {

            setBalanceLoading(
              false
            );

          }

        };


      loadBalance();

    },
    [
      api,
      open,
      form.employeeId,
      form.fromDate,
    ]
  );


  if (
    !open
  ) {

    return null;

  }


  const handleChange = (
    event
  ) => {

    const {
      name,
      value,
      checked,
      type,
    } =
      event.target;


    setForm(
      (
        previous
      ) => ({
        ...previous,

        [name]:
          type ===
          "checkbox"
            ?
            checked
            :
            value,
      })
    );


    setErrors(
      (
        previous
      ) => ({
        ...previous,

        [name]:
          "",

        form:
          "",
      })
    );

  };


  const calculateDays =
    () => {

      if (
        !form.fromDate ||
        !form.toDate
      ) {

        return 0;

      }


      const start =
        new Date(
          `${form.fromDate}T00:00:00`
        );


      const end =
        new Date(
          `${form.toDate}T00:00:00`
        );


      if (
        end <
        start
      ) {

        return 0;

      }


      if (
        form.halfDay
      ) {

        return 0.5;

      }


      return (
        Math.floor(
          (
            end -
            start
          ) /
          86400000
        ) +
        1
      );

    };


  const totalDays =
    calculateDays();


  const selectedLeaveType =
    leaveTypes.find(
      (
        type
      ) =>
        String(
          type._id
        ) ===
        String(
          form.leaveTypeId
        )
    );


  const selectedBalance =
    balances.find(
      (
        balance
      ) =>
        String(
          balance?.leaveTypeId?._id ||
          balance?.leaveTypeId
        ) ===
        String(
          form.leaveTypeId
        )
    );


  const validate =
    () => {

      const nextErrors =
        {};


      if (
        !form.employeeId
      ) {

        nextErrors.employeeId =
          "Employee is required.";

      }


      if (
        !form.leaveTypeId
      ) {

        nextErrors.leaveTypeId =
          "Leave type is required.";

      }


      if (
        !form.fromDate
      ) {

        nextErrors.fromDate =
          "From date is required.";

      }


      if (
        !form.toDate
      ) {

        nextErrors.toDate =
          "To date is required.";

      }


      if (
        form.fromDate &&
        form.toDate &&
        new Date(
          form.toDate
        ) <
        new Date(
          form.fromDate
        )
      ) {

        nextErrors.toDate =
          "To date cannot be before from date.";

      }


      if (
        form.halfDay &&
        form.fromDate !==
        form.toDate
      ) {

        nextErrors.toDate =
          "Half-day leave must use the same start and end date.";

      }


      if (
        !form.reason.trim()
      ) {

        nextErrors.reason =
          "Leave reason is required.";

      }


      setErrors(
        nextErrors
      );


      return (
        Object.keys(
          nextErrors
        ).length ===
        0
      );

    };


  const handleSubmit =
    async (
      event
    ) => {

      event.preventDefault();


      if (
        !validate()
      ) {

        return;

      }


      try {

        setLoading(
          true
        );


        const response =
          await api.post(
            config.createEmployeeLeave,
            {
              employeeId:
                form.employeeId,

              leaveTypeId:
                form.leaveTypeId,

              fromDate:
                form.fromDate,

              toDate:
                form.toDate,

              halfDay:
                form.halfDay,

              halfDaySession:
                form.halfDay
                  ?
                  form.halfDaySession
                  :
                  null,

              reason:
                form.reason.trim(),

              remark:
                form.remark.trim(),
            }
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to create leave."
          );

        }


        await onSuccess?.(
          response?.data
        );

      } catch (
        requestError
      ) {

        setErrors({
          form:
            getEmployeeApiError(
              requestError,
              "Unable to create leave."
            ),
        });

      } finally {

        setLoading(
          false
        );

      }

    };


  return ReactDOM
    .createPortal(

      <div className="employee-leave-modal-root">

        <button
          type="button"
          className="employee-leave-modal-backdrop"
          onClick={
            loading
              ?
              undefined
              :
              onClose
          }
          aria-label="Close"
        />


        <form
          className="employee-leave-modal"
          onSubmit={
            handleSubmit
          }
        >

          <header>

            <div>

              <span>
                <FiCalendar />
              </span>


              <div>

                <small>
                  EMPLOYEE LEAVE
                </small>

                <h3>
                  Add Leave
                </h3>

              </div>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
            >
              <FiX />
            </button>

          </header>


          <div className="employee-leave-modal-body">

            {
              errors.form
                ?
                (
                  <div className="employee-leave-modal-error">
                    {errors.form}
                  </div>
                )
                :
                null
            }


            <div className="employee-leave-form-grid">

              <div className="employee-leave-form-full">

                <EmployeeSelector
                  employees={
                    employees
                  }
                  value={
                    form.employeeId
                  }
                  onChange={
                    (
                      employeeId
                    ) => {

                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,
                          employeeId,
                        })
                      );


                      setErrors(
                        (
                          previous
                        ) => ({
                          ...previous,
                          employeeId: "",
                        })
                      );

                    }
                  }
                  label="Employee"
                  required
                  error={
                    errors.employeeId
                  }
                />

              </div>


              <div className="employee-leave-form-field">

                <label>
                  Leave Type *
                </label>

                <select
                  name="leaveTypeId"
                  value={
                    form.leaveTypeId
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="">
                    Select leave type
                  </option>

                  {
                    leaveTypes
                      .filter(
                        (
                          type
                        ) =>
                          type?.status !==
                          "INACTIVE"
                      )
                      .map(
                        (
                          type
                        ) => (

                          <option
                            key={
                              type._id
                            }
                            value={
                              type._id
                            }
                          >
                            {type.name}
                          </option>

                        )
                      )
                  }

                </select>

                {
                  errors.leaveTypeId
                    ?
                    (
                      <small>
                        {errors.leaveTypeId}
                      </small>
                    )
                    :
                    null
                }

              </div>


              <div className="employee-leave-form-field employee-leave-day-box">

                <label>
                  Requested Days
                </label>

                <div>
                  {totalDays}
                </div>

              </div>


              <div className="employee-leave-form-field">

                <label>
                  From Date *
                </label>

                <input
                  type="date"
                  name="fromDate"
                  value={
                    form.fromDate
                  }
                  onChange={
                    (
                      event
                    ) => {

                      handleChange(
                        event
                      );


                      if (
                        form.halfDay
                      ) {

                        setForm(
                          (
                            previous
                          ) => ({
                            ...previous,

                            fromDate:
                              event
                                .target
                                .value,

                            toDate:
                              event
                                .target
                                .value,
                          })
                        );

                      }

                    }
                  }
                />

                {
                  errors.fromDate
                    ?
                    (
                      <small>
                        {errors.fromDate}
                      </small>
                    )
                    :
                    null
                }

              </div>


              <div className="employee-leave-form-field">

                <label>
                  To Date *
                </label>

                <input
                  type="date"
                  name="toDate"
                  value={
                    form.toDate
                  }
                  min={
                    form.fromDate ||
                    undefined
                  }
                  onChange={
                    handleChange
                  }
                  disabled={
                    form.halfDay
                  }
                />

                {
                  errors.toDate
                    ?
                    (
                      <small>
                        {errors.toDate}
                      </small>
                    )
                    :
                    null
                }

              </div>


              <div className="employee-leave-form-full">

                <label className="employee-leave-checkbox">

                  <input
                    type="checkbox"
                    name="halfDay"
                    checked={
                      form.halfDay
                    }
                    onChange={
                      (
                        event
                      ) => {

                        const checked =
                          event.target.checked;


                        setForm(
                          (
                            previous
                          ) => ({
                            ...previous,

                            halfDay:
                              checked,

                            toDate:
                              checked
                                ?
                                previous.fromDate
                                :
                                previous.toDate,
                          })
                        );

                      }
                    }
                  />

                  <span />

                  <div>

                    <strong>
                      Half Day Leave
                    </strong>

                    <small>
                      Mark this request as half-day leave.
                    </small>

                  </div>

                </label>

              </div>


              {
                form.halfDay
                  ?
                  (
                    <div className="employee-leave-form-field employee-leave-form-full">

                      <label>
                        Half Day Session
                      </label>

                      <select
                        name="halfDaySession"
                        value={
                          form.halfDaySession
                        }
                        onChange={
                          handleChange
                        }
                      >

                        <option value="FIRST_HALF">
                          First Half
                        </option>

                        <option value="SECOND_HALF">
                          Second Half
                        </option>

                      </select>

                    </div>
                  )
                  :
                  null
              }


              <div className="employee-leave-form-field employee-leave-form-full">

                <label>
                  Leave Reason *
                </label>

                <textarea
                  name="reason"
                  value={
                    form.reason
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter leave reason"
                />

                {
                  errors.reason
                    ?
                    (
                      <small>
                        {errors.reason}
                      </small>
                    )
                    :
                    null
                }

              </div>


              <div className="employee-leave-form-field employee-leave-form-full">

                <label>
                  Internal Remark
                </label>

                <textarea
                  name="remark"
                  value={
                    form.remark
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Optional admin remark"
                />

              </div>

            </div>


            {
              selectedLeaveType
                ?
                (
                  <div className="employee-leave-type-preview">

                    <div>

                      <span>
                        Leave Type
                      </span>

                      <strong>
                        {
                          selectedLeaveType.name
                        }
                      </strong>

                    </div>


                    <div>

                      <span>
                        Paid Leave
                      </span>

                      <strong>
                        {
                          selectedLeaveType.isPaid
                            ?
                            "Yes"
                            :
                            "No"
                        }
                      </strong>

                    </div>


                    <div>

                      <span>
                        Annual Allocation
                      </span>

                      <strong>
                        {
                          Number(
                            selectedLeaveType
                              ?.allowedDays ??
                            selectedLeaveType
                              ?.annualDays ??
                            0
                          )
                        }
                      </strong>

                    </div>


                    <div>

                      <span>
                        Remaining
                      </span>

                      <strong>

                        {
                          balanceLoading
                            ?
                            "..."
                            :
                            Number(
                              selectedBalance
                                ?.remainingDays ??
                              selectedBalance
                                ?.balance ??
                              0
                            )
                        }

                      </strong>

                    </div>

                  </div>
                )
                :
                null
            }

          </div>


          <footer>

            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                loading
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              disabled={
                loading
              }
            >

              <FiSave />

              {
                loading
                  ?
                  "Saving..."
                  :
                  "Create Leave"
              }

            </button>

          </footer>

        </form>

      </div>,

      document.body

    );

};


export default AddLeaveModal;